import ArrowForward from '@mui/icons-material/ArrowForward';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import Check from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import { Button } from '@src/common/components/button/Button';
import { FormSelect } from '@src/common/components/FormSelect';
import { Stack } from '@src/common/components/Stack';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import type { CompletePermitSchemaType } from '@src/permits/completePermit/fields/CompletePermitFields';
import { isNoteRequired } from '@src/permits/completePermit/fields/CompletePermitFields';
import {
  CompleteAction,
  CompletionTypeField,
} from '@src/permits/completePermit/fields/CompletePermitFields';
import type { Control, UseFormGetValues } from 'react-hook-form';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props {
  control: Control<CompletePermitSchemaType>;
  getValues: UseFormGetValues<CompletePermitSchemaType>;
  onClickConfirmDelivery: () => void;
}

//TODO Given form is in error, it does not render again.
//As a result the button is not updated even when selecting another completion type.
// It remains with the old label until form becomes ok, then it updates
export const CompletePermitActions = ({
  control,
  getValues,
  onClickConfirmDelivery,
}: Props) => {
  const { t } = useTranslation();
  const { isValid } = useFormState({ control });
  const selectedAction = getValues(CompletionTypeField.fieldName)
    ? CompleteAction[getValues(CompletionTypeField.fieldName)]
    : '';

  const actionIconStyle = { marginRight: theme.spacing['3'] };
  const requireNote = isNoteRequired(selectedAction);

  const submitButtonContent = requireNote ? (
    <>
      {t('next')}
      <ArrowForward style={{ marginLeft: theme.spacing['2'] }} />
    </>
  ) : (
    <>
      <Check style={{ marginRight: theme.spacing['2'] }} />
      {t('confirm')}
    </>
  );

  return (
    <Stack
      direction="row"
      justifyContent="end"
      alignItems="center"
      gap={theme.spacing['2']}
    >
      <Stack width="400px">
        <FormSelect
          label={t(`action`)}
          fieldName={CompletionTypeField.fieldName}
          options={CompletionTypeField.options.map((option) => ({
            value: option.valueOf(),
            label: (
              <>
                {option === CompleteAction.DELIVER && (
                  <Stack direction="row" alignItems="center">
                    <MarkEmailReadOutlinedIcon style={actionIconStyle} />
                    {t('complete')}
                  </Stack>
                )}
                {option === CompleteAction.FLAG_ERROR && (
                  <Stack direction="row" alignItems="center">
                    <ErrorOutlineIcon style={actionIconStyle} />
                    {t('flagError')}
                  </Stack>
                )}
                {option === CompleteAction.DENY && (
                  <Stack direction="row" alignItems="center">
                    <CancelOutlinedIcon style={actionIconStyle} />
                    {t('deny')}
                  </Stack>
                )}
              </>
            ),
          }))}
          control={control}
        />
      </Stack>

      {selectedAction === CompleteAction.DELIVER ? (
        <Button
          onClick={onClickConfirmDelivery}
          variant="contained"
          className={button}
          disabled={!isValid}
        >
          {submitButtonContent}
        </Button>
      ) : (
        <Button
          type="submit"
          variant="contained"
          className={button}
          disabled={!isValid}
        >
          {submitButtonContent}
        </Button>
      )}
    </Stack>
  );
};

const button = css({
  padding: '0 18px',
  height: '40px',
});
